/**
 * 会員ポイント決済コンポーネント
 */

import React, { useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  LinearProgress,
} from "@mui/material";
import {
  Stars as PointsIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

// ランク設定
const RANK_CONFIG = {
  bronze: { label: "ブロンズ", color: "#CD7F32", rate: "1%" },
  silver: { label: "シルバー", color: "#C0C0C0", rate: "1.5%" },
  gold: { label: "ゴールド", color: "#FFD700", rate: "2%" },
  platinum: { label: "プラチナ", color: "#E5E4E2", rate: "3%" },
};

const RANK_THRESHOLDS = {
  bronze: 0,
  silver: 10000,
  gold: 50000,
  platinum: 100000,
};

const MemberPointPayment = ({
  orderId,
  orderTotal,
  storeId,
  onPaymentComplete,
  onCancel,
  mode = "use", // "use" | "earn" | "both"
}) => {
  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [memberInfo, setMemberInfo] = useState(null);
  const [usePoints, setUsePoints] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // 会員検索
  const handleSearchMember = useCallback(async () => {
    if (!searchQuery.trim()) {
      setError("電話番号または会員IDを入力してください");
      return;
    }

    setIsSearching(true);
    setError(null);
    setMemberInfo(null);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `/api/member-points/search?storeId=${storeId}&query=${encodeURIComponent(searchQuery)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "会員の検索に失敗しました");
      }

      if (data.members.length === 0) {
        setError("会員が見つかりませんでした");
        return;
      }

      // 最初の会員を選択（本来は選択UIが必要）
      const member = data.members[0];

      // 会員詳細を取得
      const detailResponse = await fetch(`/api/member-points/${member._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const detailData = await detailResponse.json();

      if (!detailResponse.ok) {
        throw new Error(detailData.message || "会員詳細の取得に失敗しました");
      }

      setMemberInfo(detailData.member);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, storeId]);

  // ポイント使用
  const handleUsePoints = useCallback(async () => {
    const points = parseInt(usePoints, 10);

    if (!points || points <= 0) {
      setError("使用ポイントを入力してください");
      return;
    }

    if (points > memberInfo.points) {
      setError(`ポイントが不足しています（残高: ${memberInfo.points}pt）`);
      return;
    }

    const pointValue = points; // 1ポイント = 1円
    if (pointValue > orderTotal) {
      setError(`使用ポイントは注文金額（¥${orderTotal.toLocaleString()}）相当以下にしてください`);
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/member-points/${memberInfo._id}/use`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          points,
          orderId,
          description: `注文 #${orderId?.slice(-6) || "N/A"}`,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "ポイントの使用に失敗しました");
      }

      setResult({
        type: "use",
        usedPoints: data.usedPoints,
        discountAmount: data.discountAmount,
        currentPoints: data.currentPoints,
      });
      setIsComplete(true);

      // 親コンポーネントに通知
      if (onPaymentComplete) {
        onPaymentComplete({
          method: "points",
          amount: data.discountAmount,
          pointsUsed: data.usedPoints,
          memberId: memberInfo.memberId,
          remainingPoints: data.currentPoints,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  }, [memberInfo, usePoints, orderId, onPaymentComplete, orderTotal]);

  // ポイント付与のみ
  const handleEarnPoints = useCallback(async () => {
    setIsProcessing(true);
    setError(null);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`/api/member-points/${memberInfo._id}/earn`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          orderAmount: orderTotal,
          orderId,
          description: `注文 #${orderId?.slice(-6) || "N/A"}`,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "ポイントの付与に失敗しました");
      }

      setResult({
        type: "earn",
        earnedPoints: data.earnedPoints,
        currentPoints: data.currentPoints,
        rank: data.rank,
      });
      setIsComplete(true);

      // 親コンポーネントに通知
      if (onPaymentComplete) {
        onPaymentComplete({
          method: "points_earn",
          earnedPoints: data.earnedPoints,
          memberId: memberInfo.memberId,
          newRank: data.rank,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  }, [memberInfo, orderTotal, orderId, onPaymentComplete]);

  // 次のランクまでの進捗計算
  const getNextRankProgress = (lifetimePoints, currentRank) => {
    const ranks = ["bronze", "silver", "gold", "platinum"];
    const currentIndex = ranks.indexOf(currentRank);

    if (currentIndex >= ranks.length - 1) {
      return { nextRank: null, progress: 100, pointsNeeded: 0 };
    }

    const nextRank = ranks[currentIndex + 1];
    const currentThreshold = RANK_THRESHOLDS[currentRank];
    const nextThreshold = RANK_THRESHOLDS[nextRank];
    const progress = ((lifetimePoints - currentThreshold) / (nextThreshold - currentThreshold)) * 100;
    const pointsNeeded = nextThreshold - lifetimePoints;

    return { nextRank, progress: Math.min(100, Math.max(0, progress)), pointsNeeded };
  };

  // 完了画面
  if (isComplete && result) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {result.type === "use" ? "ポイント利用完了" : "ポイント付与完了"}
          </Typography>

          {result.type === "use" ? (
            <>
              <Typography variant="h4" color="primary" sx={{ my: 2 }}>
                {result.usedPoints.toLocaleString()}pt 利用
              </Typography>
              <Typography variant="body1">
                割引額: ¥{result.discountAmount.toLocaleString()}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h4" color="success.main" sx={{ my: 2 }}>
                +{result.earnedPoints.toLocaleString()}pt
              </Typography>
              <Chip
                label={RANK_CONFIG[result.rank]?.label || result.rank}
                sx={{
                  bgcolor: RANK_CONFIG[result.rank]?.color,
                  color: result.rank === "gold" ? "black" : "white",
                }}
              />
            </>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            現在のポイント: {result.currentPoints.toLocaleString()}pt
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {/* ヘッダー */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <PointsIcon sx={{ fontSize: 32, mr: 1, color: "primary.main" }} />
        <Typography variant="h6">
          {mode === "earn" ? "ポイント付与" : mode === "use" ? "ポイント利用" : "会員ポイント"}
        </Typography>
      </Box>

      {/* 注文金額 */}
      <Alert severity="info" sx={{ mb: 3 }}>
        お支払い金額: ¥{orderTotal.toLocaleString()}
        {mode !== "use" && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            ※ランクに応じてポイントが付与されます
          </Typography>
        )}
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 会員検索フォーム */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="電話番号 / 会員ID / 氏名"
            placeholder="090-1234-5678"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={isSearching || isProcessing || !!memberInfo}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              ),
              endAdornment: !memberInfo && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleSearchMember}
                    disabled={isSearching || !searchQuery.trim()}
                  >
                    {isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          {!memberInfo && (
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearchMember}
              disabled={isSearching || !searchQuery.trim()}
              sx={{ height: "56px" }}
            >
              {isSearching ? <CircularProgress size={24} /> : "会員検索"}
            </Button>
          )}
        </Grid>
      </Grid>

      {/* 会員情報表示 */}
      {memberInfo && (
        <Card sx={{ mt: 3, bgcolor: "grey.50" }}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                会員情報
              </Typography>
              <Chip
                label={RANK_CONFIG[memberInfo.rank]?.label || memberInfo.rank}
                sx={{
                  bgcolor: RANK_CONFIG[memberInfo.rank]?.color,
                  color: memberInfo.rank === "gold" ? "black" : "white",
                  fontWeight: "bold",
                }}
              />
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  会員ID
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {memberInfo.memberId}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  氏名
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {memberInfo.memberName || "未登録"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  保有ポイント
                </Typography>
                <Typography variant="h5" color="primary.main" fontWeight="bold">
                  {memberInfo.points.toLocaleString()}pt
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  ポイント還元率
                </Typography>
                <Typography variant="h6" color="success.main">
                  {RANK_CONFIG[memberInfo.rank]?.rate || "1%"}
                </Typography>
              </Grid>
            </Grid>

            {/* 次のランクまでの進捗 */}
            {(() => {
              const { nextRank, progress, pointsNeeded } = getNextRankProgress(
                memberInfo.lifetimePoints,
                memberInfo.rank
              );

              if (!nextRank) return null;

              return (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {RANK_CONFIG[nextRank]?.label}まで
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      あと{pointsNeeded.toLocaleString()}pt
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "grey.300",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: RANK_CONFIG[nextRank]?.color,
                      },
                    }}
                  />
                </Box>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* ポイント使用入力 */}
      {memberInfo && (mode === "use" || mode === "both") && (
        <>
          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom>
            ポイントを使用する
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="使用ポイント"
                type="number"
                value={usePoints}
                onChange={(e) => setUsePoints(e.target.value)}
                disabled={isProcessing}
                InputProps={{
                  endAdornment: <InputAdornment position="end">pt</InputAdornment>,
                }}
                helperText={`1pt = ¥1 / 最大: ${Math.min(memberInfo.points, orderTotal).toLocaleString()}pt`}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setUsePoints(Math.min(memberInfo.points, orderTotal).toString())}
                disabled={isProcessing}
                sx={{ height: "56px" }}
              >
                全額使用
              </Button>
            </Grid>
          </Grid>

          {/* 残額表示 */}
          {usePoints && parseInt(usePoints, 10) > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    ポイント割引
                  </Typography>
                  <Typography variant="h6" color="primary.main">
                    -¥{parseInt(usePoints, 10).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    残りのお支払い
                  </Typography>
                  <Typography variant="h6" color={orderTotal - parseInt(usePoints, 10) > 0 ? "warning.main" : "success.main"}>
                    ¥{Math.max(0, orderTotal - parseInt(usePoints, 10)).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleUsePoints}
              disabled={isProcessing || !usePoints || parseInt(usePoints, 10) <= 0}
            >
              {isProcessing ? (
                <CircularProgress size={24} />
              ) : (
                `${parseInt(usePoints, 10) || 0}ポイントを使用する`
              )}
            </Button>
          </Box>
        </>
      )}

      {/* ポイント付与のみ */}
      {memberInfo && mode === "earn" && (
        <>
          <Divider sx={{ my: 3 }} />

          <Box sx={{ p: 2, bgcolor: "success.light", borderRadius: 1, textAlign: "center" }}>
            <Typography variant="body2" color="success.dark">
              今回のお買い物で付与されるポイント
            </Typography>
            <Typography variant="h4" color="success.dark" fontWeight="bold">
              +{Math.floor(orderTotal * (parseFloat(RANK_CONFIG[memberInfo.rank]?.rate) / 100 || 0.01)).toLocaleString()}pt
            </Typography>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              onClick={handleEarnPoints}
              disabled={isProcessing}
            >
              {isProcessing ? <CircularProgress size={24} /> : "ポイントを付与する"}
            </Button>
          </Box>
        </>
      )}

      {/* 別の会員を検索 / キャンセル */}
      <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "center" }}>
        {memberInfo && (
          <Button
            variant="text"
            onClick={() => {
              setMemberInfo(null);
              setSearchQuery("");
              setUsePoints("");
            }}
            disabled={isProcessing}
          >
            別の会員を検索
          </Button>
        )}
        <Button variant="text" onClick={onCancel} disabled={isProcessing}>
          キャンセル
        </Button>
      </Box>
    </Paper>
  );
};

export default MemberPointPayment;
